import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AzButtonComponent } from '../az-button/az-button.component';
import { AzSelectorComponent } from '../az-selector/az-selector.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { ScrollActivityDirective } from '../../directives/scroll-activity.directive';

@Component({
  selector: 'app-az-document-update-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AzButtonComponent,
    AzSelectorComponent,
    SpinnerComponent,
    ScrollActivityDirective,
  ],
  templateUrl: './az-document-update-modal.component.html',
  styleUrl: './az-document-update-modal.component.scss',
})
export class AzDocumentUpdateModalComponent {
  @Input() isOpen: boolean = false;
  @Input() isUploading: boolean = false;
  @Input() documentTypes: string[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() upload = new EventEmitter<{
    file: File | null;
    documentType: string;
    comments: string;
  }>();

  selectedFile: File | null = null;
  selectedDocumentType: string = '';
  comments: string = '';
  isDragging: boolean = false;
  errorMessage: string = '';

  readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  readonly ALLOWED_EXTENSIONS = ['.docx', '.xlsx'];

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.validateAndSetFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.validateAndSetFile(event.dataTransfer.files[0]);
    }
  }

  validateAndSetFile(file: File): void {
    this.errorMessage = '';

    // Validate file extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = this.ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));

    if (!hasValidExtension) {
      this.errorMessage = 'Invalid file format. Please upload .docx or .xlsx files only.';
      this.selectedFile = null;
      return;
    }

    // Validate file size
    if (file.size > this.MAX_FILE_SIZE) {
      this.errorMessage = 'File size exceeds 10MB. Please upload a smaller file.';
      this.selectedFile = null;
      return;
    }

    // File is valid
    this.selectedFile = file;
  }

  removeFile(): void {
    this.selectedFile = null;
    this.errorMessage = '';
  }

  get selectedFileName(): string {
    return this.selectedFile ? this.selectedFile.name : '';
  }

  get selectedFileSize(): string {
    if (!this.selectedFile) return '';
    const sizeInMB = (this.selectedFile.size / (1024 * 1024)).toFixed(2);
    return `${sizeInMB} MB`;
  }

  onCancel(): void {
    this.resetForm();
    this.close.emit();
  }

  onConfirm(): void {
    this.upload.emit({
      file: this.selectedFile,
      documentType: this.selectedDocumentType,
      comments: this.comments,
    });
    this.resetForm();
  }

  onDocumentTypeChange(type: string): void {
    this.selectedDocumentType = type;
  }

  private resetForm(): void {
    this.selectedFile = null;
    this.selectedDocumentType = '';
    this.comments = '';
    this.isDragging = false;
    this.errorMessage = '';
  }
}
